import React, { useCallback, useEffect, useState } from 'react';
import { PageHeader, Card, DataTable, Badge, Button, Toast } from '../ui';
import {
  listChannels,
  listLocales,
  getInternalRule,
  updateInternalRule,
  getLocaleGroups,
  updateLocaleGroups,
  listClassifications,
} from '../../services/pimApi';
import './pim.css';

const splitCsv = (text) =>
  text
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

export default function PimSettings() {
  const [channels, setChannels] = useState([]);
  const [locales, setLocales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  // Internal-product rule
  const [prefixesText, setPrefixesText] = useState('');
  const [selectedClasses, setSelectedClasses] = useState(() => new Set());
  const [classifications, setClassifications] = useState([]);
  const [savingRule, setSavingRule] = useState(false);

  // Enrichment locale groups
  const [groups, setGroups] = useState([]); // array of arrays of market codes
  const [savingGroups, setSavingGroups] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, l, rule, lg, cls] = await Promise.all([
        listChannels(),
        listLocales(),
        getInternalRule(),
        getLocaleGroups(),
        listClassifications(),
      ]);
      setChannels(c);
      setLocales(l);
      setPrefixesText((rule.sku_prefixes || []).join(', '));
      setSelectedClasses(new Set(rule.epicor_classifications || []));
      setGroups((lg.groups || []).map((g) => [...g]));
      setClassifications(cls.classifications || []);
    } catch (e) {
      setToast({ message: 'Failed to load settings', error: true });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggleClass = (code) => {
    setSelectedClasses((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const saveRule = async () => {
    setSavingRule(true);
    try {
      const saved = await updateInternalRule({
        sku_prefixes: splitCsv(prefixesText),
        epicor_classifications: Array.from(selectedClasses),
      });
      setPrefixesText((saved.sku_prefixes || []).join(', '));
      setSelectedClasses(new Set(saved.epicor_classifications || []));
      setToast({ message: 'Internal-product rule saved' });
    } catch (e) {
      setToast({ message: e?.response?.data?.detail || 'Save failed', error: true });
    } finally {
      setSavingRule(false);
    }
  };

  const saveGroups = async () => {
    setSavingGroups(true);
    try {
      const saved = await updateLocaleGroups(groups.map((g) => g));
      setGroups((saved.groups || []).map((g) => [...g]));
      setToast({ message: 'Locale groups saved' });
    } catch (e) {
      setToast({ message: e?.response?.data?.detail || 'Save failed', error: true });
    } finally {
      setSavingGroups(false);
    }
  };

  return (
    <>
      <PageHeader title="PIM Settings" sub="Internal-product rules, enrichment locale groups, channels and locales." />

      <Card
        title="Internal products"
        desc="Internal products stay in the PIM but are hard-excluded from BigCommerce. Matched by SKU prefix (case-insensitive) or Epicor classification."
        actions={<Button onClick={saveRule} disabled={savingRule || loading}>{savingRule ? 'Saving…' : 'Save rule'}</Button>}
      >
        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
          SKU prefixes (comma-separated)
        </label>
        <input
          type="text"
          value={prefixesText}
          onChange={(e) => setPrefixesText(e.target.value)}
          placeholder="zz, lb"
          style={{ width: '100%', maxWidth: 420, padding: '8px 10px', marginBottom: 16 }}
        />
        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
          Excluded Epicor classifications
        </label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {classifications.map((c) => (
            <label key={c.code} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              <input
                type="checkbox"
                checked={selectedClasses.has(c.code)}
                onChange={() => toggleClass(c.code)}
              />
              <code>{c.code}</code>
              <span className="pim-muted">({c.n})</span>
            </label>
          ))}
          {classifications.length === 0 && <span className="pim-muted">No classifications found.</span>}
        </div>
      </Card>

      <div style={{ height: 18 }} />

      <Card
        title="Enrichment locale groups"
        desc="Markets in a group share one enriched product_name / description copy. A manual edit to one member propagates to the others on save."
        actions={<Button onClick={saveGroups} disabled={savingGroups || loading}>{savingGroups ? 'Saving…' : 'Save groups'}</Button>}
      >
        {groups.map((g, idx) => (
          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <input
              type="text"
              value={g.join(', ')}
              onChange={(e) => {
                const next = groups.slice();
                next[idx] = splitCsv(e.target.value).map((m) => m.toUpperCase());
                setGroups(next);
              }}
              placeholder="AU, NZ"
              style={{ width: '100%', maxWidth: 360, padding: '8px 10px' }}
            />
            <Button variant="secondary" onClick={() => setGroups(groups.filter((_, i) => i !== idx))}>
              Remove
            </Button>
          </div>
        ))}
        <Button variant="secondary" onClick={() => setGroups([...groups, []])}>+ Add group</Button>
        {groups.length === 0 && <p className="pim-muted" style={{ marginTop: 8 }}>No groups — each market is enriched independently.</p>}
      </Card>

      <div style={{ height: 18 }} />

      <Card title="Channels (regions)" desc="Each channel scopes per-region values and per-region publish flags.">
        <DataTable
          loading={loading}
          rows={channels}
          rowKey={(r) => r.id}
          columns={[
            { key: 'code', header: 'Code', render: (r) => <code>{r.code}</code> },
            { key: 'label', header: 'Label' },
            { key: 'market_code', header: 'Region', render: (r) => <Badge variant="info">{r.market_code}</Badge> },
            { key: 'currency', header: 'Currency' },
            { key: 'akeneo_channel_code', header: 'Akeneo scope', render: (r) => r.akeneo_channel_code || '—' },
            { key: 'default_locale_code', header: 'Default locale', render: (r) => r.default_locale_code || '—' },
          ]}
        />
      </Card>

      <div style={{ height: 18 }} />

      <Card title="Locales" desc="Languages available for localizable attributes.">
        <DataTable
          loading={loading}
          rows={locales}
          rowKey={(r) => r.id}
          columns={[
            { key: 'code', header: 'Code', render: (r) => <code>{r.code}</code> },
            { key: 'label', header: 'Label' },
            { key: 'enabled', header: 'Enabled', render: (r) => (r.enabled ? 'Yes' : 'No') },
          ]}
        />
      </Card>

      {toast && <Toast message={toast.message} error={toast.error} />}
    </>
  );
}
